import type { ReactElement, SVGProps } from 'react';
import type { ChainNetwork } from '@ducat-unit/core';
import { toast } from 'sonner';

import { getConfig } from '@/config/config';
import { getFrontendNetwork } from '@/config/deployment';
import { swapNotificationShowsContinue } from '@/stores/notification.helpers';
import { useAppStore } from '@/stores/useAppStore';
import { markAsViewed } from '@/stores/useNotificationStore';
import type { NotificationActionButton, NotificationActionLink, NotificationMetadata, NotificationType } from '@/types/notification.types';

import type Icons from './Icons';
import { resolveIcon } from './notification-icons';
import { skipSwapNotification } from './notification-swap-actions';

// ─────────────────────────────────────────────────────────────
// Hydrated types (with React elements, used at render time only)
// ─────────────────────────────────────────────────────────────

export interface HydratedActionButton {
  dismissOnClick?: boolean;
  label: string;
  variant: 'primary' | 'secondary';
  href?: string;
  trailingIcon?: ReactElement<SVGProps<SVGSVGElement>>;
  leadingIcon?: ReactElement<SVGProps<SVGSVGElement>>;
  onClick?: () => void;
}

export interface HydratedActionLink {
  label: string;
  href: string;
  target?: string;
  color?: string;
  secondaryText?: string;
  icon?: ReactElement<SVGProps<SVGSVGElement>>;
}

const getMempoolUrl = (network: ChainNetwork) => {
  const { mempoolUrl } = getConfig(network) || {};
  return mempoolUrl;
};

const hydrateActionButton = (
  { dismissOnClick, label, variant, href, trailingIcon, leadingIcon, appAction }: NotificationActionButton,
  notificationId?: string | number,
): HydratedActionButton => ({
  dismissOnClick,
  label,
  variant,
  href,
  trailingIcon: resolveIcon(trailingIcon),
  leadingIcon: resolveIcon(leadingIcon),
  ...(appAction === 'open_swap_drawer'
    ? {
        onClick: () => {
          useAppStore.getState().requestOpenSwapDrawer();
        },
      }
    : {}),
  ...(appAction === 'skip_pending_swap'
    ? {
        onClick: () => {
          void skipSwapNotification(notificationId ?? '');
        },
      }
    : {}),
});

const createTxDetailsButton = (
  label: string,
  txid: string,
  notificationId?: string,
  network: ChainNetwork = getFrontendNetwork(),
): HydratedActionButton => ({
  label,
  variant: 'secondary',
  href: `${getMempoolUrl(network)}/tx/${txid}`,
  trailingIcon: resolveIcon('externalLink'),
  ...(notificationId && {
    onClick: () => {
      toast.dismiss(notificationId);
      markAsViewed(notificationId);
    },
  }),
});

const createTgNotifyButton = (label: string, telegramOpenUrl: string, notificationId?: string): HydratedActionButton => ({
  label,
  variant: 'secondary',
  href: telegramOpenUrl,
  leadingIcon: resolveIcon('telegram'),
  onClick: () => {
    toast.dismiss(notificationId);
    markAsViewed(notificationId);
  },
});

/** Localized labels for the buttons this module generates itself. */
export interface GeneratedButtonLabels {
  details: string;
  enableTelegram: string;
}

/**
 * Hydrate serializable action buttons into render-ready buttons with React elements
 */
export const hydrateActionButtons = (
  labels: GeneratedButtonLabels,
  actions: NotificationActionButton[] = [],
  metadata?: NotificationMetadata,
  notificationId?: string | number,
  network?: ChainNetwork,
  actionLinks?: { label: string }[],
): HydratedActionButton[] => {
  const txDetailsButton = metadata?.txid ? [createTxDetailsButton(labels.details, metadata.txid, String(notificationId), network)] : [];
  const tgNotifyButton = metadata?.telegramOpenUrl
    ? [createTgNotifyButton(labels.enableTelegram, metadata.telegramOpenUrl, String(notificationId))]
    : [];
  const visibleActions = swapNotificationShowsContinue({ metadata, actionLinks })
    ? actions
    : actions.filter((action) => action.appAction !== 'open_swap_drawer' && action.appAction !== 'skip_pending_swap');
  const hydratedActions = visibleActions.map((action) => hydrateActionButton(action, notificationId));

  return [...hydratedActions, ...txDetailsButton, ...tgNotifyButton];
};

export const hydrateActionLinks = (links: NotificationActionLink[] = []): HydratedActionLink[] => {
  return links.map(({ label, href, target, color, icon, secondaryText }) => ({
    label,
    href,
    target,
    color,
    secondaryText,
    icon: resolveIcon(icon),
  }));
};

// ─────────────────────────────────────────────────────────────
// Progress bar utilities
// ─────────────────────────────────────────────────────────────

/** Progress bar update interval in milliseconds (20fps for smooth animation) */
export const PROGRESS_UPDATE_INTERVAL = 50;

/** Calculate progress percentage based on elapsed time */
export const calculateProgress = (startTime: number, pausedTime: number, duration: number): number => {
  const now = Date.now();
  const elapsed = now - startTime - pausedTime;
  const remaining = Math.max(0, duration - elapsed);

  return (remaining / duration) * 100;
};

/** Clamp progress value between 0 and 100 */
export const clampProgress = (value: number): number => {
  return Math.max(0, Math.min(100, value));
};

// ─────────────────────────────────────────────────────────────
// Toast type to icon mapping
// ─────────────────────────────────────────────────────────────

export const TYPE_TO_ICON_MAP: Record<NotificationType, keyof typeof Icons> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
  progress: 'Progress',
  quanta: 'Quanta',
  dropped: 'Warning',
};

/** Prefer SwapPending (static) over Progress spinner for unfinished SwapDrawer items. */
export const resolveNotificationIconKey = (
  type: NotificationType,
  metadata?: NotificationMetadata,
  actionLinks?: { label: string }[],
): keyof typeof Icons => {
  if (type === 'progress' && metadata?.swapFlow) {
    return swapNotificationShowsContinue({ metadata, actionLinks }) ? 'SwapPendingAction' : 'SwapPending';
  }
  return TYPE_TO_ICON_MAP[type];
};

// ─────────────────────────────────────────────────────────────
// Global hover state management (for progress bar sync with Sonner)
// ─────────────────────────────────────────────────────────────

// Sonner pauses ALL toasts when ANY is hovered, we track this to sync progress bars
const hoveredToasts = new Set<string | number>();
export const hoverListeners = new Set<(isHovered: boolean) => void>();

/** Check if any toast is currently hovered */
export const getIsAnyToastHovered = (): boolean => {
  return hoveredToasts.size > 0;
};

/** Check if specific toast is currently hovered */
export const getIsToastHovered = (toastId: string | number): boolean => {
  return hoveredToasts.has(toastId);
};

/** Notify all listeners about hover state change */
const notifyHoverChange = (): void => {
  if (hoverListeners.size === 0) {
    return;
  }

  for (const listener of hoverListeners) {
    listener(hoveredToasts.size > 0);
  }
};

/** Handle mouse enter on toast */
export const handleMouseEnter = (toastId: string | number): void => {
  if (hoveredToasts.has(toastId)) {
    return;
  }

  const wasEmpty = hoveredToasts.size === 0;
  hoveredToasts.add(toastId);

  // Notify only when transitioning from 0 to 1 (no hover -> hover)
  if (wasEmpty) {
    notifyHoverChange();
  }
};

/** Handle mouse leave on toast */
export const handleMouseLeave = (toastId: string | number): void => {
  if (!hoveredToasts.has(toastId)) {
    return;
  }

  hoveredToasts.delete(toastId);

  // Notify only when transitioning from 1 to 0 (hover -> no hover)
  if (hoveredToasts.size === 0) {
    notifyHoverChange();
  }
};
